import { Alert, AlertTitle, LinearProgress } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { Database } from "../../__definitions__/database-definitions";
import { EventCrudDataGrid } from "../../modules/event/components/event-crud-data-grid";
import { EventCrudDataGridPerson } from "../../modules/event/components/event-crud-data-grid-person";
import {
  type AccountPersonField,
  SupabaseAccountPersonFieldService,
} from "../../modules/event/server/supabase-account-person-field-service";
import {
  SupabaseEventsWithMomentsCalendarService,
  SupabaseEventsWithMomentsPersonService,
} from "../../modules/event/server/supabase-event-service";
import { RouteTabs } from "../../modules/generic/components/route-tabs";
import { Page } from "../../modules/layout/components/page";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { FieldsCrudDataGrid } from "../../modules/merge-tag/components/fields-crud-data-grid";
import { useSupabase } from "../../server/supabase/hooks";

const DefaultPage: FC<{ tab?: "events" | "merge_tags" }> = ({ tab }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (tab) void navigate(`/manage/employee_data/${tab}`, { replace: true });
  });
  return null;
};

const Events: FC = () => {
  const [events, setEvents] =
    useState<Database["public"]["Views"]["events_with_related_moments_calendar"]["Row"][]>();
  const [hasError, setHasError] = useState<boolean>(false);

  useSupabase(async ({ supabase, account_id }) => {
    const { data, error } = await new SupabaseEventsWithMomentsCalendarService(supabase).getAll(
      account_id,
      { order: [{ column: "id" }] },
    );
    if (!data || error) return setHasError(true);

    setEvents(data);
  }, []);

  return (
    <>
      {hasError && (
        <Alert severity="error">
          <AlertTitle>Something went wrong</AlertTitle>
        </Alert>
      )}
      {events === undefined ? <LinearProgress /> : <EventCrudDataGrid events={events} />}
    </>
  );
};

const PersonEvents: FC = () => {
  const [events, setEvents] =
    useState<Database["public"]["Views"]["events_with_related_moments_person"]["Row"][]>();
  const [hasError, setHasError] = useState<boolean>(false);

  useSupabase(async ({ supabase, account_id }) => {
    const { data, error } = await new SupabaseEventsWithMomentsPersonService(supabase).getAll(
      account_id,
      { order: [{ column: "id" }] },
    );
    if (!data || error) return setHasError(true);

    setEvents(data);
  }, []);

  return (
    <>
      {hasError && (
        <Alert severity="error">
          <AlertTitle>Something went wrong</AlertTitle>
        </Alert>
      )}
      {events === undefined ? <LinearProgress /> : <EventCrudDataGridPerson events={events} />}
    </>
  );
};

const MergeTags: FC = () => {
  const [fields, setFields] = useState<AccountPersonField[]>();
  const [hasError, setHasError] = useState<boolean>(false);

  useSupabase(async ({ supabase, account_id }) => {
    const { data, error } = await new SupabaseAccountPersonFieldService(supabase).getAll(
      account_id,
      { order: [{ column: "id" }] },
    );
    if (!data || error) return setHasError(true);

    setFields(data);
  }, []);

  return (
    <>
      {hasError && (
        <Alert severity="error">
          <AlertTitle>Something went wrong</AlertTitle>
        </Alert>
      )}
      {fields === undefined ? <LinearProgress /> : <FieldsCrudDataGrid fields={fields} />}
    </>
  );
};

const ManageEventsAndTagsPage: FC = () => {
  return (
    <Page title="Manage Events & Tags | ChangeEngine">
      <PageHeader>
        <PageHeading heading="Events & Merge Tags" subheading="Manage your events and merge tags" />
      </PageHeader>
      <RouteTabs
        base="/manage/employee_data/"
        defaultPage={<DefaultPage tab="events" />}
        tabs={[
          {
            type: "explicit",
            data: { name: "Calendar Events", route: "events" },
            component: <Events />,
          },
          { type: "inferred", data: { name: "Person Events" }, component: <PersonEvents /> },
          { type: "inferred", data: { name: "Merge Tags" }, component: <MergeTags /> },
        ]}
      />
    </Page>
  );
};

export default ManageEventsAndTagsPage;
